import { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, X, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { UpsellSuggestion } from '@/hooks/useUpsellSuggestions';

interface UpsellPopoverProps {
  suggestions: UpsellSuggestion[];
  sourceProductId: string;
  sourceProductName: string;
  onAddToOrder: (suggestion: UpsellSuggestion) => void;
  onDismiss: () => void;
  onShown?: (suggestion: UpsellSuggestion) => void;
  onAccepted?: (suggestion: UpsellSuggestion) => void;
  autoHideDelay?: number; // milliseconds, default 8000 (8 seconds)
  compact?: boolean; // Default true - compact mode is less intrusive
  className?: string;
}

export function UpsellPopover({
  suggestions,
  sourceProductId: _sourceProductId, // Used for tracking context, may be needed for future analytics
  sourceProductName,
  onAddToOrder,
  onDismiss,
  onShown,
  onAccepted,
  autoHideDelay = 8000,
  compact = true,
  className,
}: UpsellPopoverProps) {
  const { formatCurrency } = useCurrency();
  const [isVisible, setIsVisible] = useState(true);
  // Use ref instead of state to track shown items to avoid infinite loops
  const trackedShownRef = useRef<Set<string>>(new Set());

  // Auto-hide after delay
  useEffect(() => {
    if (!isVisible || suggestions.length === 0) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      onDismiss();
    }, autoHideDelay);

    return () => clearTimeout(timer);
  }, [isVisible, suggestions.length, autoHideDelay, onDismiss]);

  // Track shown events for each suggestion (runs once when suggestions change)
  useEffect(() => {
    if (!onShown || suggestions.length === 0) return;

    suggestions.forEach(suggestion => {
      if (!trackedShownRef.current.has(suggestion.product_id)) {
        trackedShownRef.current.add(suggestion.product_id);
        // Fire and forget - don't await
        try {
          onShown(suggestion);
        } catch (e) {
          // Silently ignore tracking errors
        }
      }
    });
  }, [suggestions, onShown]);

  const handleAdd = useCallback((suggestion: UpsellSuggestion) => {
    try {
      onAccepted?.(suggestion);
    } catch (e) {
      // Silently ignore tracking errors
    }
    onAddToOrder(suggestion);
  }, [onAccepted, onAddToOrder]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    onDismiss();
  }, [onDismiss]);

  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  // Get the primary suggestion (first one)
  const primarySuggestion = suggestions[0];
  const hasMultiple = suggestions.length > 1;

  // Determine the icon based on association type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'manual':
        return <Sparkles className="h-3 w-3" />;
      case 'computed':
        return <TrendingUp className="h-3 w-3" />;
      default:
        return null;
    }
  };

  // Get default prompt text based on context
  const getPromptText = (suggestion: UpsellSuggestion) => {
    if (suggestion.prompt_text) return suggestion.prompt_text;
    if (suggestion.association_type === 'computed') {
      return `Often ordered with ${sourceProductName}`;
    }
    return `Pairs great with ${sourceProductName}`;
  };

  // Compact mode: minimal, single-line display
  if (compact) {
    return (
      <div
        className={cn(
          "absolute bottom-full left-0 right-0 mb-2 z-50",
          "animate-in slide-in-from-bottom-2 fade-in duration-200",
          className
        )}
      >
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 shadow-md backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-400 truncate">
              Add {primarySuggestion.product_name}?
            </span>
            <span className="text-xs font-semibold text-primary shrink-0">
              {formatCurrency(primarySuggestion.product_price)}
            </span>
            <Button
              size="sm"
              variant="default"
              className="h-6 px-2 text-xs gap-1 bg-amber-500 hover:bg-amber-600 text-white shrink-0 ml-auto"
              onClick={() => handleAdd(primarySuggestion)}
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-foreground shrink-0"
              onClick={handleDismiss}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          {/* Compact progress bar */}
          <div className="mt-1.5 h-0.5 bg-amber-500/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500/50 rounded-full animate-shrink-width"
              style={{ 
                animationDuration: `${autoHideDelay}ms`,
                animationTimingFunction: 'linear'
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Expanded mode: full display with images and multiple suggestions
  return (
    <div
      className={cn(
        "absolute bottom-full left-0 right-0 mb-2 z-50",
        "animate-in slide-in-from-bottom-2 fade-in duration-200",
        className
      )}
    >
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/30 rounded-xl p-3 shadow-lg backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              Suggested Add-on
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        {/* Primary Suggestion */}
        <div className="flex items-center gap-3">
          {/* Product Image */}
          {primarySuggestion.product_image_url ? (
            <img
              src={primarySuggestion.product_image_url}
              alt={primarySuggestion.product_name}
              className="w-14 h-14 rounded-lg object-cover border border-border/50"
            />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center border border-border/50">
              <span className="text-lg font-bold text-muted-foreground">
                {primarySuggestion.product_name.charAt(0)}
              </span>
            </div>
          )}

          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">
              {primarySuggestion.product_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {getPromptText(primarySuggestion)}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-semibold text-sm text-primary">
                {formatCurrency(primarySuggestion.product_price)}
              </span>
              {getTypeIcon(primarySuggestion.association_type) && (
                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  {getTypeIcon(primarySuggestion.association_type)}
                  {primarySuggestion.association_type === 'computed' && 'Popular'}
                </span>
              )}
            </div>
          </div>

          {/* Add Button */}
          <Button
            size="sm"
            variant="default"
            className="gap-1 bg-amber-500 hover:bg-amber-600 text-white shrink-0"
            onClick={() => handleAdd(primarySuggestion)}
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        </div>

        {/* Additional Suggestions (if multiple) */}
        {hasMultiple && (
          <div className="mt-2 pt-2 border-t border-amber-500/20">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {suggestions.slice(1, 3).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleAdd(suggestion)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-background/50 hover:bg-background border border-border/50 hover:border-primary/30 transition-colors shrink-0"
                >
                  {suggestion.product_image_url ? (
                    <img
                      src={suggestion.product_image_url}
                      alt={suggestion.product_name}
                      className="w-8 h-8 rounded object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                      <span className="text-xs font-bold text-muted-foreground">
                        {suggestion.product_name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-xs font-medium truncate max-w-[100px]">
                      {suggestion.product_name}
                    </p>
                    <p className="text-[10px] text-primary font-semibold">
                      {formatCurrency(suggestion.product_price)}
                    </p>
                  </div>
                  <Plus className="h-3 w-3 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Progress bar for auto-dismiss */}
        <div className="mt-2 h-0.5 bg-amber-500/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-amber-500/50 rounded-full animate-shrink-width"
            style={{ 
              animationDuration: `${autoHideDelay}ms`,
              animationTimingFunction: 'linear'
            }}
          />
        </div>
      </div>
    </div>
  );
}

